import { SparkSuspense } from '@sparklayer-io/spark-dash-core/components/spark-suspense';
import { DEFAULT_CURRENCY } from '@sparklayer-io/spark-dash-core/constants/currency';
import {
  CUSTOM_RANGE_VALUE,
  type CustomDateRange,
  defaultCustomDateRange,
  resolveDateRange,
  toDateInputValue,
} from '@sparklayer-io/spark-dash-core/utils/date-range';
import { formatMoneyRounded } from '@sparklayer-io/spark-dash-core/utils/format-money';
import type { DropdownOption } from '@sparklayer-io/spark-dash-ui/components/ui/dropdown/types';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Card, Dropdown, EmptyState, Icon, Input, Pressable, Text } from '@ui/themed';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { SalesPerformanceChart } from '../../../components/sales-performance-chart';
import { useHasEverPurchased } from '../../purchases/hooks/use-has-ever-purchased';
import type { SalesMetric } from '../../purchases/hooks/use-sales-chart-data';
import {
  type SalesComparisonReport,
  useSalesPerformance,
} from '../../purchases/hooks/use-sales-performance';
import type { PurchaseType } from '../../purchases/types';
import { percentChange } from '../lib/percent-change';

function getDateOptions(): DropdownOption[] {
  return [
    {
      label: t('features:home.components.sales_performance_card.date_options.last_7_days'),
      value: '7',
    },
    {
      label: t('features:home.components.sales_performance_card.date_options.last_30_days'),
      value: '30',
    },
    {
      label: t('features:home.components.sales_performance_card.date_options.last_90_days'),
      value: '90',
    },
  ];
}

function getCustomDateOption(): DropdownOption {
  return {
    label: t('features:home.components.sales_performance_card.date_options.custom'),
    value: CUSTOM_RANGE_VALUE,
  };
}

const PURCHASE_TYPES: Exclude<PurchaseType, 'cart'>[] = [
  'all',
  'order',
  'quote',
  'awaiting_approval',
  'awaiting_merchant',
  'archived',
  'platform_archived',
];

function getPurchaseOptions() {
  return PURCHASE_TYPES.map((purchaseType) => ({
    label: t(`features:home.components.sales_performance_card.purchase_types.${purchaseType}`),
    value: purchaseType,
  }));
}

function calculateSalesStats(data?: SalesComparisonReport): SalesStat[] {
  if (!data) {
    return [
      {
        title: t('features:home.components.sales_performance_card.stats_cards.orders'),
        metric: 'num_orders',
      },
      {
        title: t('features:home.components.sales_performance_card.stats_cards.total_sales'),
        metric: 'total_val',
      },
      {
        title: t('features:home.components.sales_performance_card.stats_cards.avg_order_val'),
        metric: 'avg_order_val',
      },
    ] satisfies SalesStat[];
  }

  const { current, previous } = data;
  const currentOrders = current.data.reduce((sum, d) => sum + d.count, 0);
  const previousOrders = previous.data.reduce((sum, d) => sum + d.count, 0);

  const currentTotalSales = current.data.reduce((sum, d) => sum + Number(d.total.number), 0);
  const previousTotalSales = previous.data.reduce((sum, d) => sum + Number(d.total.number), 0);

  const currentAverageOrderValue = currentOrders === 0 ? 0 : currentTotalSales / currentOrders;
  const previousAverageOrderValue = previousOrders === 0 ? 0 : previousTotalSales / previousOrders;

  const currency = current.data[0]?.total.currency ?? DEFAULT_CURRENCY;

  return [
    {
      title: t('features:home.components.sales_performance_card.stats_cards.orders'),
      value: currentOrders.toString(),
      percentageChange: percentChange(currentOrders, previousOrders),
      metric: 'num_orders',
    },
    {
      title: t('features:home.components.sales_performance_card.stats_cards.total_sales'),
      value: formatMoneyRounded(currentTotalSales, currency),
      percentageChange: percentChange(currentTotalSales, previousTotalSales),
      metric: 'total_val',
    },
    {
      title: t('features:home.components.sales_performance_card.stats_cards.avg_order_val'),
      value: formatMoneyRounded(currentAverageOrderValue, currency),
      percentageChange: percentChange(currentAverageOrderValue, previousAverageOrderValue),
      metric: 'avg_order_val',
    },
  ];
}

type SalesStat = {
  title: string;
  metric: SalesMetric;
  value?: string;
  percentageChange?: number;
};

type SalesStatCardProps = {
  title: string;
  value?: string;
  percentageChange?: number;

  selected?: boolean;
  onSelected: () => void;
};

function SalesStatCard({
  title,
  value,
  percentageChange,
  selected,
  onSelected,
}: SalesStatCardProps) {
  return (
    <Pressable type="card" fullWidth fullHeight onClick={onSelected}>
      <Card variant={selected ? 'secondary' : 'base'} size="sm" fullHeight>
        <Card.Header headingLevel="h3">{title}</Card.Header>
        <Card.Body>
          <div className="grid grid-cols-[auto_1fr] gap-0.5">
            <Text bold>{value}</Text>
            {!!percentageChange && (
              // polaris doesn't have this color, so make a custom one
              <span
                className={cn(
                  'flex items-center font-semibold text-xs',
                  percentageChange > 0 ? 'text-[#00AD2B]' : 'text-spark-text-danger',
                )}
              >
                <Icon name={percentageChange > 0 ? 'increase' : 'decrease'} size="xs" />
                {`${percentageChange.toString()}%`}
              </span>
            )}
          </div>
        </Card.Body>
      </Card>
    </Pressable>
  );
}

export type SalesPerformanceCardProps = {
  nDays?: string;
  onNDaysChange?: (nDays: string) => void;
  customRange?: CustomDateRange;
  onCustomRangeChange?: (range: CustomDateRange) => void;
  /** When enabled, adds a "Custom" option that reveals start/end date inputs. */
  allowCustomRange?: boolean;
  purchaseType?: PurchaseType;
  onPurchaseTypeChange?: (type: PurchaseType) => void;
};

export function SalesPerformanceCard(props: SalesPerformanceCardProps = {}) {
  const [internalNDays, setInternalNDays] = useState<string>(props.nDays ?? '7');
  const [internalCustomRange, setInternalCustomRange] = useState<CustomDateRange>(
    props.customRange ?? defaultCustomDateRange,
  );
  const [internalPurchaseType, setInternalPurchaseType] = useState<PurchaseType>(
    props.purchaseType ?? 'all',
  );

  const nDays = props.nDays ?? internalNDays;
  const setNDays = (val: string) => {
    setInternalNDays(val);
    props.onNDaysChange?.(val);
  };

  const customRange = props.customRange ?? internalCustomRange;
  const setCustomRange = (range: CustomDateRange) => {
    setInternalCustomRange(range);
    props.onCustomRangeChange?.(range);
  };

  const purchaseType = props.purchaseType ?? internalPurchaseType;
  const setPurchaseType = (val: PurchaseType) => {
    setInternalPurchaseType(val);
    props.onPurchaseTypeChange?.(val);
  };

  const handleDateOptionChange = (val: string) => {
    // Seed the custom inputs with the currently selected window when switching to a custom
    // range, so the picker starts from a sensible point rather than an empty/stale range.
    if (val === CUSTOM_RANGE_VALUE && nDays !== CUSTOM_RANGE_VALUE) {
      const range = resolveDateRange(nDays, customRange);
      setCustomRange({ start: toDateInputValue(range.start), end: toDateInputValue(range.end) });
    }
    setNDays(val);
  };

  const { start, end } = useMemo(() => resolveDateRange(nDays, customRange), [nDays, customRange]);

  const hasEverPurchased = useHasEverPurchased();
  const salesPerformance = useSalesPerformance({ start, end, type: purchaseType });

  const salesStats = useMemo(
    () => calculateSalesStats(salesPerformance.data),
    [salesPerformance.data],
  );

  // Remove any purchase options that aren't in their history
  const filteredPurchaseOptions = getPurchaseOptions().filter(
    (o) => o.value === 'all' || o.value in (salesPerformance.data?.countsByType ?? {}),
  );

  const [selectedMetric, setSelectedMetric] = useState<SalesMetric>('total_val');

  const dateOptions = props.allowCustomRange
    ? [...getDateOptions(), getCustomDateOption()]
    : getDateOptions();
  const isCustomRange = nDays === CUSTOM_RANGE_VALUE;
  const today = toDateInputValue(new Date());
  const rangeError =
    isCustomRange && customRange.start && customRange.end && customRange.start > customRange.end
      ? t('features:home.components.sales_performance_card.custom_range.end_before_start_error')
      : undefined;

  // Filters and stats only mean something once there is a purchase to filter
  if (!hasEverPurchased.data) {
    return (
      <Card>
        <SparkSuspense
          isLoading={hasEverPurchased.isLoading}
          isError={hasEverPurchased.isError}
          errorMessage={t('errors:generic')}
          onReload={hasEverPurchased.refetch}
        >
          <EmptyState
            kind="empty"
            compact
            title={t('features:home.components.sales_performance_card.empty_state.title')}
            description={t('features:home.components.sales_performance_card.empty_state.subtitle')}
            illustration="orders"
            primaryAction={null}
          />
        </SparkSuspense>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <Dropdown options={dateOptions} value={nDays} onChange={handleDateOptionChange} />
          {isCustomRange && (
            <>
              <Input
                type="date"
                short
                label={t(
                  'features:home.components.sales_performance_card.custom_range.start_label',
                )}
                value={customRange.start}
                max={customRange.end || today}
                onChange={(value) => setCustomRange({ ...customRange, start: value })}
              />
              <Input
                type="date"
                short
                label={t('features:home.components.sales_performance_card.custom_range.end_label')}
                value={customRange.end}
                min={customRange.start}
                max={today}
                error={rangeError}
                onChange={(value) => setCustomRange({ ...customRange, end: value })}
              />
            </>
          )}
          <Dropdown
            options={filteredPurchaseOptions}
            value={purchaseType}
            onChange={(val) => setPurchaseType(val as PurchaseType)}
          />
        </div>

        <SparkSuspense
          isLoading={salesPerformance.isLoading}
          isError={salesPerformance.isError}
          errorMessage={t('errors:generic')}
          onReload={salesPerformance.refetch}
        >
          <div className="grid w-fit grid-cols-1 gap-3 sm:grid-cols-3">
            {salesStats.map((s) => (
              <SalesStatCard
                key={s.title}
                selected={selectedMetric === s.metric}
                onSelected={() => setSelectedMetric(s.metric)}
                {...s}
              />
            ))}
          </div>

          {salesPerformance.data && (
            <SalesPerformanceChart data={salesPerformance.data} metric={selectedMetric} />
          )}
        </SparkSuspense>
      </div>
    </Card>
  );
}
